'use client';

import type { ReactNode } from 'react';
import type { VideoUrlResult } from 'src/types/search-all/video-url';
import type { SimilarAdItem } from 'src/actions/meta-ads/meta-ads-similar';

import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { toPreLineHtml } from 'src/utils/text/html-text';
import { formatNumber } from 'src/utils/format/format-utils';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';

import { RADIUS, SHADOW } from 'src/theme/ui-tokens';
import { CREATOR_CHANNEL_VALUES } from 'src/constants/creator-finder-options';

import { Iconify } from 'src/components/iconify';
import { MediaShot } from 'src/components/cmui/media-shot';
import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { SegmentTabs } from 'src/components/cmui/segment-tabs';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import PostKindChips from 'src/components/cmui/post-card/PostKindChips';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';
import PostMetricTrend from 'src/components/cmui/post-card/PostMetricTrend';
import { ShareLinkButton } from 'src/components/cmui/public-share/share-link-button';
import { NegativeMentionList } from 'src/components/cmui/post-card/negative-mention-list';
import { PUBLIC_SHARE_BACKDROP_SX } from 'src/components/cmui/public-share/public-share-backdrop';
import PostTranslateBlock, { type PostTranslate } from 'src/components/cmui/post-card/PostTranslateBlock';

import ProductDetailBody from 'src/sections/market/components/market-rankings-detail-body';

import { MediaCarousel } from './media-carousel';
import { MediaTextFace } from './media-text-face';
import { canShareMedia } from '../utils/search-all-share-link';
import { toMediaSlides } from '../utils/search-all-media-slides';
import { readAdRun, findAdTwin } from '../utils/search-all-ad-run';
import { toRelativeLabel, toBookmarkTarget } from '../utils/search-all-media-cards';
import { useMediaDetailProfile } from '../hooks/load/fetch/use-media-detail-profile';
import { splitDetailText, buildDetailPanel } from '../utils/search-all-media-detail';
import { MEDIA_DETAIL_CTA, useMediaDetailTrack } from '../hooks/handler/use-media-detail-track';

import type { MediaCard } from '../utils/search-all-media-cards';
import type { MediaDetailOptions } from '../utils/search-all-detail-options';
import type { MediaDetailCta } from '../hooks/handler/use-media-detail-track';
import type { SearchAllProductDetail } from '../hooks/load/fetch/use-search-all-product-detail';

/**
 * 소재 상세 다이얼로그 — 왼쪽 큰 실물(영상은 재생 · 멈춰 있을 때 마우스로 장면 훑기) · 오른쪽 정보 패널 · 양옆 이전/다음 · 오른쪽 위 닫기.
 *
 * 정보 패널(위→아래): 머리(얼굴 · 이름 · 날짜 · 채널) → 본문(줄 수를 넘치면 「더 보기」 · 같은 글은 한 번만 — `splitDetailText`) → 번역 보기 → 광고면 CTA → 성과 숫자(오가닉) · 핵심 사실 두 칸 · 누적 통계 추이 · 태그 묶음(`buildDetailPanel`) → 발(원문 · 공유 링크 · 북마크).
 * 발치 한 줄: 유사 광고 · 유사 컨텐츠를 탭으로 갈아 끼우고 접을 수 있다 — `SimilarStrip`.
 * 광고는 열 때 원본 문서(AI 판별)를 받아 채운다. 키보드 ←/→ 로도 넘긴다.
 * 글 목록 화면은 `options` 로 검색어 형광 · 「캠페인 추가」 · 수치 시점을 더 싣는다(`MediaDetailOptions`).
 * 화면이 제 글에만 더 싣는 칸은 `extraSection` 으로 받아 누적 통계 추이 위에 세운다 — 안 주거나 null 이면 자리도 없다.
 * 🔑 그리는 것만 한다 — 상세가 쥐는 값(원본 문서 · 비슷한 소재 · 상품 상세 · 영상 주소)은 부르는 쪽이 props 로 준다(`HubMediaDetail` 이 묻는 쪽).
 *   밖으로 나가는 동작(허브로 이동 · 번역 · 북마크 저장)도 갈아 끼울 수 있다(`onNavigate` · `translate` · `bookmarkLock`) — 안 주면 실화면 동작이다.
 * 🔑 활동 로그는 여기 한 곳에서 남긴다(`useMediaDetailTrack`) — 카드를 보일 때마다 `item_opened`, 허브 문은 `cta_clicked`.
 *   이 상세를 쓰는 모든 화면이 따로 적지 않는다. 체험 화면은 `analytics={false}` 로 끈다.
 */

/** 상품 상세 본문이 읽는 것 — 상품 정보 조사와 같은 질문 셋의 결과(`useSearchAllProductDetail`) */
export type MediaDetailProduct = Pick<SearchAllProductDetail, 'product' | 'history' | 'cross' | 'detail' | 'reloadDetail'>;

const CLAMP_LINES = 6;

/** 크리에이터 허브가 다루는 SNS 다섯인가 — 블로그·카페·지식인 글쓴이는 크리에이터가 아니다 */
function isCreatorChannel(channel: string | undefined): boolean {
  return (CREATOR_CHANNEL_VALUES as readonly string[]).includes(channel ?? '');
}

function textOf(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function listOf(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [];
}

/** 칠할 것 — 화면이 준 검색어 셋 + 글이 가진 감성 근거(긍정 파랑 · 부정 빨강). 하나도 없으면 `null` */
type Paint = { searchKeyword: string; clientSearch: string; topicKeywords: string[]; positive: string[]; negative: string[] };

function paintOf(card: MediaCard | null, options: MediaDetailOptions | undefined): Paint | null {
  if (card?.kind !== 'organic') return null;
  const hl = options?.highlight ?? null;
  const positive = listOf(card.post?.sentimentEvidencePositive);
  const negative = readNegativeEvidence(card.post).filter((x) => x.trim() !== '');
  if (!hl && positive.length === 0 && negative.length === 0) return null;
  return { searchKeyword: hl?.searchKeyword ?? '', clientSearch: hl?.clientSearch ?? '', topicKeywords: hl?.topicKeywords ?? [], positive, negative };
}

/** 칠한 본문 — 줄 바꿈 태그만 개행으로 바꾸고 칠한 뒤 `SafeHtml` 이 세척해 그린다 */
function PaintedText({ text, paint }: { text: string; paint: Paint }) {
  const html = highlightTextWithHtmlRemoval(toPreLineHtml(text), paint.searchKeyword, paint.topicKeywords, paint.clientSearch, paint.positive, paint.negative) ?? '';
  return <SafeHtml html={html} />;
}

const SIMILAR_THUMB = { width: 84, height: 104 };

function SimilarThumb({ image, title, sub, onClick }: { image: string; title: string; sub: string; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        flexShrink: 0,
        width: SIMILAR_THUMB.width,
        p: 0,
        border: 0,
        bgcolor: 'transparent',
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover .similar-face': { boxShadow: SHADOW.overlay, transform: 'translateY(-2px)' },
      }}
    >
      <Box
        className="similar-face"
        sx={{ transition: (theme) => theme.transitions.create(['box-shadow', 'transform'], { duration: 150 }), borderRadius: RADIUS.sm }}
      >
        <MediaShot src={image} {...SIMILAR_THUMB} />
      </Box>
      <Typography variant="caption" noWrap component="div" sx={{ mt: 0.5, fontWeight: 600 }}>{title || '—'}</Typography>
      <Typography variant="caption" noWrap component="div" color="text.secondary">{sub}</Typography>
    </Box>
  );
}

type SimilarLane = 'ads' | 'posts';

const SIMILAR_LANE_LABEL: Record<SimilarLane, string> = { ads: '유사 광고', posts: '유사 컨텐츠' };

/**
 * 다이얼로그 발치의 「비슷한 소재」 — **한 줄만 편다.**
 *
 * 🔑 두 갈래를 **탭으로 갈아 끼운다** — 줄을 둘 다 세워 두었더니 발치가 400px 가까이 먹어
 *   정작 소재를 볼 자리가 줄었다. 한 번에 보는 것은 한 줄이면 된다.
 * 🔑 **접을 수 있다** — 접으면 머리 한 줄만 남는다. 소재를 크게 보려는 사람이 되찾는 자리다.
 * 🔑 탭은 늘 둘 다 세운다 — 없는 쪽은 자리를 남기고 막는다(`disabled`). 빼면 왜 없는지 알 길이 없다.
 * ⚠️ 받는 중에도 자리를 지킨다 — 받고 나서 줄이 생기면 화면이 튄다.
 * ⚠️ 둘 다 없어도 줄을 치우지 않는다 — 탭 둘을 막은 채 같은 높이로 두고 「비슷한 소재가 없습니다. 다른 소재를 열어 보세요.」를 적는다(카드를 넘길 때 발치가 튀지 않게).
 * 🔑 수는 탭 안(`count`)에 적는다 — 받는 동안은 작은 스피너(`countLoading`) · 다 받은 뒤에 세운다 · 없으면 「0」(`showZero`). 두 갈래 모두 썸네일 장수라 단위가 같다.
 */
function SimilarStrip({
  similar,
  onOpenAd,
  onOpenCard,
}: {
  similar: { ads: SimilarAdItem[]; adsLoading: boolean; posts: MediaCard[]; postsLoading: boolean };
  onOpenAd: (adId: string) => void;
  onOpenCard: (card: MediaCard) => void;
}) {
  const [lane, setLane] = useState<SimilarLane>('ads');
  const [open, setOpen] = useState(true);

  const adsShown = similar.adsLoading || similar.ads.length > 0;
  const postsShown = similar.postsLoading || similar.posts.length > 0;
  // 고른 갈래가 이 카드에 없으면 있는 쪽을 편다 — 카드를 갈아 끼우면 갈래가 바뀐다
  const active: SimilarLane = lane === 'ads' && !adsShown ? 'posts' : lane === 'posts' && !postsShown ? 'ads' : lane;
  const loading = active === 'ads' ? similar.adsLoading : similar.postsLoading;
  const empty = !adsShown && !postsShown;

  return (
    <Stack spacing={1} sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2, pt: 1, pb: open ? 1.5 : 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <SegmentTabs<SimilarLane>
          value={active}
          onChange={setLane}
          tabs={[
            { key: 'ads', label: SIMILAR_LANE_LABEL.ads, count: similar.adsLoading ? undefined : similar.ads.length, countLoading: similar.adsLoading, showZero: true, disabled: !adsShown },
            { key: 'posts', label: SIMILAR_LANE_LABEL.posts, count: similar.postsLoading ? undefined : similar.posts.length, countLoading: similar.postsLoading, showZero: true, disabled: !postsShown },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="inherit"
          onClick={() => setOpen((prev) => !prev)}
          endIcon={<Iconify icon={open ? 'solar:alt-arrow-down-linear' : 'solar:alt-arrow-up-linear'} width={14} />}
          sx={{ typography: 'uiAction', color: 'text.secondary', flexShrink: 0 }}
        >
          {open ? '접기' : '펴기'}
        </Button>
      </Stack>

      {open && (
        <Stack direction="row" spacing={1.5} alignItems={empty ? 'center' : undefined} justifyContent={empty ? 'center' : undefined} sx={{ overflowX: 'auto', minHeight: SIMILAR_THUMB.height + 34, pb: 0.5 }}>
          {empty && <Typography variant="body2" color="text.secondary">비슷한 소재가 없습니다. 다른 소재를 열어 보세요.</Typography>}
          {loading && Array.from({ length: 10 }, (_, i) => <Box key={i} sx={{ ...SIMILAR_THUMB, flexShrink: 0, borderRadius: RADIUS.sm, bgcolor: 'action.hover' }} />)}
          {!loading && active === 'ads' && similar.ads.map((ad) => (
            <SimilarThumb
              key={ad.adArchiveId}
              image={ad.previewImage ?? ''}
              title={ad.pageName ?? ''}
              sub={ad.isActive ? '집행 중' : ''}
              onClick={() => onOpenAd(ad.adArchiveId)}
            />
          ))}
          {!loading && active === 'posts' && similar.posts.map((p) => (
            <SimilarThumb key={p.key} image={p.thumb} title={p.name} sub={toRelativeLabel(p.time)} onClick={() => onOpenCard(p)} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * 태그 묶음 중 **누를 수 있는 것** — 브랜드만 갈 곳이 있다(성분·고민·해시태그는 없다).
 *
 * ⚠️ 이름이 곧 짝이다 — `search-all-media-detail` 이 짓는 묶음 제목과 **한 글자도 달라지면 안 된다**.
 * ⚠️ 갈래마다 제목이 다르다 — 오가닉은 「브랜드」, 광고는 「언급 브랜드」다. 하나만 적으면
 *   한쪽 갈래에서 조용히 안 눌린다.
 */
const BRAND_GROUPS = new Set(['브랜드', '언급 브랜드']);

/** 허브로 건너뛰는 단추 — 채운 검정. 상세에서 **나가는 문**이라 눈에 걸려야 한다 */
const HUB_LINK_SX = {
  height: 30,
  px: 1.5,
  borderRadius: RADIUS.pill,
  typography: 'uiAction',
  whiteSpace: 'nowrap',
  maxWidth: 1,
  boxShadow: 'none',
  bgcolor: 'text.primary',
  color: 'common.white',
  '&:hover': { bgcolor: 'common.black', boxShadow: 'none' },
  '& .MuiButton-startIcon': { mr: 0.5 },
  '& .MuiButton-endIcon': { ml: 0.25, opacity: 0.7 },
} as const;

const RUN_CHIP_SX = {
  px: 0.875,
  height: 22,
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: RADIUS.pill,
  typography: 'uiLabel',
} as const;

export function MediaDetailDialog({
  card: given,
  video,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
  onShare,
  adDoc,
  similar,
  onOpenSimilarAd,
  onOpenSimilarCard,
  publicView = false,
  productDetail = null,
  options,
  onNavigate,
  translate,
  bookmarkLock = '',
  analytics = true,
  extraSection,
}: {
  card: MediaCard | null;
  /** 새로 받은 영상 주소 결과 — 없으면 `undefined` */
  video: VideoUrlResult | undefined;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onShare: (card: MediaCard) => void;
  /** 광고 원본 문서 — 받는 중이면 `null` */
  adDoc: Record<string, unknown> | null;
  similar: { ads: SimilarAdItem[]; adsLoading: boolean; posts: MediaCard[]; postsLoading: boolean };
  /** 비슷한 광고를 눌렀을 때 — 그 광고 원본을 받아 다이얼로그를 갈아 끼운다 */
  onOpenSimilarAd: (adId: string) => void;
  /** 비슷한 게시물을 눌렀을 때 — 이미 카드라 바로 갈아 끼운다 */
  onOpenSimilarCard: (card: MediaCard) => void;
  /** 공유 페이지(비회원) — 이전/다음·북마크를 감추고 뒤를 흐린다. 닫기는 된다 */
  publicView?: boolean;
  /** 상품 상세(상품 정보 조사와 같은 질문 셋) — 상품 카드만 */
  productDetail?: MediaDetailProduct | null;
  /** 글 목록 화면이 더 싣는 것 — 검색어 형광 · 「캠페인 추가」 · 수치 시점. 없으면 소재 찾기 그대로 */
  options?: MediaDetailOptions;
  /** 허브로 건너뛸 때 — 경로를 받는다. 안 주면 이 창에서 옮긴다(`router.push`) */
  onNavigate?: (href: string) => void;
  /** 「번역 보기」가 부르는 번역 — 안 주면 AI 번역(`PostTranslateBlock` 기본) */
  translate?: PostTranslate;
  /** 북마크를 막는 까닭 — 있으면 단추가 눌리지 않고 풍선으로 이 말을 적는다 */
  bookmarkLock?: string;
  /** 활동 로그를 남기나 — 실사용이 아닌 자리(체험 화면)만 끈다 */
  analytics?: boolean;
  /** 지금 카드에 더 싣는 칸 — 오가닉 카드에만 부른다. null 을 주면 그 카드는 기본 모양 */
  extraSection?: (card: MediaCard) => ReactNode;
}) {
  // 목록이 얼굴을 못 붙여 온 글 카드도 상세에서는 그 계정 얼굴을 찾아 붙인다(발굴 색인만)
  const card = useMediaDetailProfile(given, !publicView);
  const [expanded, setExpanded] = useState(false);
  /** 본문이 접힌 줄 수를 넘쳐 잘렸나 — 글자 수가 아니라 그려진 높이로 잰다(짧아도 줄이 많으면 잘린다) */
  const [overflowing, setOverflowing] = useState(false);
  /** 본문 요소 — 창이 늦게 붙어도 다시 재도록 상태로 쥔다 */
  const [bodyEl, setBodyEl] = useState<HTMLElement | null>(null);
  const router = useRouter();
  const navigate = useCallback((href: string) => (onNavigate ? onNavigate(href) : router.push(href)), [onNavigate, router]);
  const trackCta = useMediaDetailTrack(card, analytics);

  /** 플랫폼이 붙인 광고 표 — 색인 값만 본다(새 조회 없음) */
  const adRun = card?.kind === 'organic' ? readAdRun(card.post) : null;
  /** 같은 그림이 Meta 광고로도 도나 — 이미 받아 둔 「비슷한 소재」에서 **pHash 복제만** 고른다 */
  const adTwin = card?.kind === 'organic' ? findAdTwin(similar.ads) : null;

  /**
   * 글을 보다가 **그 브랜드의 허브로** 건너뛴다.
   * ⚠️ 새 창이 아니라 **이 창에서** 간다 — 공유 페이지(비회원)에서는 갈 곳이 없어 아예 안 연다.
   */
  const openBrandHub = useCallback(
    (brand: string, ctaId: MediaDetailCta) => {
      if (publicView) return;
      trackCta(ctaId, brand);
      navigate(paths.service.brandHubFresh(brand));
    },
    [publicView, navigate, trackCta],
  );

  /**
   * 그 크리에이터로 갈 **길이 있나** — 계정·채널을 둘 다 알아야 하고, 공유 페이지(비회원)는 갈 곳이 없다.
   * ⚠️ 광고·상품에는 계정이 없다. 크리에이터 허브가 다루는 SNS 다섯(`CREATOR_CHANNEL_VALUES`)이 아니면(블로그 · 카페 · 지식인 …) 크리에이터가 아니라 길이 없다.
   */
  const creatorPath = !publicView && card?.kind === 'organic' && Boolean(String(card?.post?.account ?? '').trim()) && isCreatorChannel(card?.channel);

  /** 글을 보다가 **그 크리에이터의 허브로** — 계정과 채널을 둘 다 알 때만 길이 있다 */
  const openCreatorHub = useCallback(() => {
    const account = String(card?.post?.account ?? '').trim();
    if (publicView || !account || !card?.channel || !isCreatorChannel(card.channel)) return;
    trackCta(MEDIA_DETAIL_CTA.creatorHub);
    navigate(`${paths.service.creators}?channel=${encodeURIComponent(card.channel)}&account=${encodeURIComponent(account)}`);
  }, [card, publicView, navigate, trackCta]);

  useEffect(() => {
    setExpanded(false);
  }, [card?.key]);

  useEffect(() => {
    if (!card) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [card, hasPrev, hasNext, onPrev, onNext]);

  const videoUrl = card ? card.videoSrc || (video?.status === 'ok' ? video.videoUrl : '') : '';
  const panel = card ? buildDetailPanel(card, adDoc, options?.metricBasis) : { metrics: [], facts: [], tags: [] };
  const paint = paintOf(card, options);
  const isStory = card?.kind === 'organic' && card.post?.isStory === true;

  /**
   * 이 소재가 말하는 **대표 브랜드** — 허브로 건너뛰는 단추가 이 하나를 쥔다.
   *
   * 🔑 태그 묶음이 정본이다(`buildDetailPanel` 의 「브랜드」·「언급 브랜드」) — 판별이 이미 거기서 끝났다.
   * ⚠️ 상품 카드에는 태그 묶음을 안 그리므로 색인 값을 쓴다.
   * ⚠️ 여럿이면 **첫 하나만** 단추로 세운다 — 나머지는 아래 태그가 그대로 누를 수 있다.
   */
  const brandLead = useMemo(() => {
    if (publicView || !card) return '';
    if (card.kind === 'product') return textOf((card.product as { brand?: unknown } | undefined)?.brand).trim();
    return (panel.tags.find((g) => BRAND_GROUPS.has(g.title))?.tags ?? [])[0] ?? '';
  }, [card, panel.tags, publicView]);
  const productImages = productDetail?.detail.data?.found ? productDetail.detail.data.images : [];
  const slides = card ? toMediaSlides(card, videoUrl, productImages) : [];
  // 인용·리포스트면 원글 — 게시물 상세(PostDetail)와 같은 칸
  const originRaw = card?.kind === 'organic' && card.post?.originPost && typeof card.post.originPost === 'object' ? (card.post.originPost as Record<string, unknown>) : null;
  const origin = originRaw && (textOf(originRaw.text) || textOf(originRaw.imageUrl))
    ? { text: textOf(originRaw.text), image: textOf(originRaw.imageUrl), link: textOf(originRaw.link), who: textOf(originRaw.author) || (textOf(originRaw.account) ? `@${textOf(originRaw.account)}` : '') }
    : null;
  const organicText = card?.kind === 'organic' ? splitDetailText(textOf(card.post?.title), textOf(card.post?.content)) : null;
  const heading = card ? (card.kind === 'product' ? textOf(card.product?.title) : card.kind === 'ad' ? textOf(card.ad?.title) : organicText?.heading ?? '') : '';
  const body = card ? (card.kind === 'product' ? '' : card.kind === 'ad' ? textOf(card.ad?.bodyText) || textOf(card.ad?.caption) : organicText?.body ?? '') : '';

  // 접힌 본문이 넘치는지 잰다 — 패널 폭이 바뀌어도 다시 잰다. 펼친 동안은 재지 않는다(「접기」가 남아야 한다)
  useEffect(() => {
    if (!bodyEl || expanded) return undefined;
    const measure = () => setOverflowing(bodyEl.scrollHeight > bodyEl.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl, body, expanded]);

  return (
    <Dialog
      open={Boolean(card)}
      onClose={onClose}
      maxWidth={false}
      slotProps={publicView ? { backdrop: { sx: PUBLIC_SHARE_BACKDROP_SX } } : undefined}
      /*
        ⚠️ 높이는 **고정**이다 — 왼쪽 실물이 남는 자리를 채우게 두려는 것이다(영상·세로 컷).
        ⚠️ 상품만 낮게 잡는다 — 오른쪽에 적을 것이 적어서 큰 창을 주면 **빈 자리가 통째로** 남는다
          (공유 페이지에서는 상세 본문이 안 와서 더 그렇다).
      */
      /*
        🚨 **폰에서는 통째로 구른다.** 넓은 화면은 키를 못 박고 본문 칸만 안에서 구르는 것이 맞지만,
          폰에서 같은 짜임이면 그림·머리·단추 줄·유사 목록이 화면을 다 먹고 **본문만 좁은 창에서**
          구른다. 읽는 자리가 손가락 두 마디가 된다.
        ⛔ 폰에서 `height` 를 못 박지 마라 — 키를 내용이 정하고, 넘치면 종이가 구른다.
        ⛔ 유사 광고·컨텐츠 줄과 단추 줄을 폰에서 **붙박이로 두지 마라** — 흘러가야 본문이 산다.
      */
      PaperProps={{ sx: {
        width: 'min(1360px, 90vw)',
        height: { xs: 'auto', md: card?.kind === 'product' ? 'min(880px, 90vh)' : 'min(1500px, 90vh)' },
        maxWidth: '100vw',
        maxHeight: { xs: '92vh', md: 'none' },
        m: { xs: 1, md: 1.5 },
        /* 넓은 화면은 **밖으로** 나온 좌우 넘김 단추 때문에 `visible` 이라야 한다(`md: -52`) */
        overflowY: { xs: 'auto', md: 'visible' },
        overflowX: 'visible',
        borderRadius: RADIUS.md,
      } }}
    >
      {card && (
        <>
          <IconButton aria-label="닫기" onClick={onClose} sx={{ position: 'fixed', top: 12, right: 12, color: 'common.white', bgcolor: 'rgba(0,0,0,0.35)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}>
            <Iconify icon="solar:close-circle-linear" />
          </IconButton>
          {hasPrev && !publicView && (
            <IconButton aria-label="이전" onClick={onPrev} sx={{ position: 'absolute', left: { xs: 4, md: -52 }, top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: 'background.paper', boxShadow: SHADOW.raised, '&:hover': { bgcolor: 'background.paper' } }}>
              <Iconify icon="solar:alt-arrow-left-linear" />
            </IconButton>
          )}
          {hasNext && !publicView && (
            <IconButton aria-label="다음" onClick={onNext} sx={{ position: 'absolute', right: { xs: 4, md: -52 }, top: '50%', transform: 'translateY(-50%)', zIndex: 2, bgcolor: 'background.paper', boxShadow: SHADOW.raised, '&:hover': { bgcolor: 'background.paper' } }}>
              <Iconify icon="solar:alt-arrow-right-linear" />
            </IconButton>
          )}

          <Stack sx={{ height: { xs: 'auto', md: 1 }, overflow: { xs: 'visible', md: 'hidden' }, borderRadius: RADIUS.md }}>
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flex: { xs: 'none', md: 1 }, minHeight: 0 }}>
            {/* ⚠️ 폰에서는 바깥이 안 잡아 주므로 그림 칸이 **제 키**를 가져야 한다(`height: 1` 인 회전목마가 접힌다) */}
            <Box sx={{ flex: { xs: 'none', md: 1 }, minWidth: 0, height: { xs: 360, md: 'auto' }, minHeight: { md: 0 }, bgcolor: card.kind === 'product' ? 'common.white' : 'grey.900', position: 'relative' }}>
              <MediaCarousel
                slides={slides}
                light={card.kind === 'product'}
                resetKey={card.key}
                empty={card.kind === 'organic' && card.title
                  ? <MediaTextFace card={card} large />
                  : <Stack alignItems="center" justifyContent="center" sx={{ height: 1, color: 'text.disabled', typography: 'body2' }}>미리보기 없음</Stack>}
              />
            </Box>

            <Stack sx={{ width: { xs: 1, md: card.kind === 'product' ? 560 : 420 }, flexShrink: 0, bgcolor: 'background.paper', minHeight: 0 }}>
              {/* 머리 줄은 누르는 곳이 아니다 — 크리에이터 허브로 가는 길은 본문 위 「크리에이터 허브에서 보기」 하나다 */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Avatar src={card.avatar || undefined} sx={{ width: 44, height: 44 }}>{card.name.slice(0, 1).toUpperCase()}</Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" noWrap>
                    {paint ? (
                      <HighlightedText
                        text={card.name}
                        searchKeyword={paint.searchKeyword}
                        topicKeywords={paint.topicKeywords}
                        clientSearch={paint.clientSearch}
                        sentimentPositive={paint.positive}
                        sentimentNegative={paint.negative}
                      />
                    ) : card.name}
                    {card.partner && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> × {card.partner}</Box>}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                    {card.kind === 'ad' && <Typography variant="caption" sx={{ fontWeight: 600 }}>광고</Typography>}
                    {card.kind === 'product' && <Typography variant="caption" sx={{ fontWeight: 600 }}>상품</Typography>}
                    <Typography variant="caption" noWrap>
                      {card.kind === 'organic' ? toRelativeLabel(card.time) : card.runLabel}
                      {card.followers != null && ` · 팔로워 ${formatNumber(card.followers)}`}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              {/* 🚨 폰에서는 **안 구른다** — 종이가 구른다(머리말). 좁은 창에 본문을 가두지 않는다 */}
              <Box sx={{ flex: { xs: 'none', md: 1 }, overflowY: { xs: 'visible', md: 'auto' }, px: 2, py: 2 }}>
                {/*
                  🔑 **돈이 들어간 글인가** — 본문보다 먼저 알린다. 오가닉 반응인 줄 알고 읽으면
                    「이 브랜드는 그냥 잘 되네」를 잘못 읽는다.
                  ⚠️ 두 줄은 **뜻이 다르다** — 위는 플랫폼이 붙인 표(그 글 자체가 광고), 아래는
                    같은 그림이 Meta 광고로도 돌더라는 대조 결과다. 섞어 적지 마라.
                */}
                {/*
                  🔑 **여기서 나가는 문을 눈에 보이게 둔다** — 소재를 보다 「이 브랜드는 요즘 어떤가」
                    「이 사람은 뭘 더 올렸나」가 생기는 자리가 바로 여기다. 크리에이터 허브로 가는 길은
                    이 단추 하나다(머리 줄은 누르지 않는다).
                  ⚠️ 공유 페이지(비회원)에는 갈 곳이 없어 아예 안 세운다.
                  ⚠️ 브랜드는 **하나만** 세운다 — 여럿을 다 세우면 단추 줄이 태그 묶음을 두 번 그린 꼴이 된다.
                */}
                {(creatorPath || brandLead) && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {creatorPath && (
                      <Button
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={openCreatorHub}
                        startIcon={<Iconify icon="solar:user-circle-linear" width={16} />}
                        endIcon={<Iconify icon="solar:alt-arrow-right-linear" width={14} />}
                        sx={HUB_LINK_SX}
                      >
                        크리에이터 허브에서 보기
                      </Button>
                    )}
                    {brandLead && (
                      <Button
                        size="small"
                        variant="contained"
                        color="inherit"
                        onClick={() => openBrandHub(brandLead, MEDIA_DETAIL_CTA.brandHub)}
                        startIcon={<Iconify icon="solar:bag-smile-linear" width={16} />}
                        endIcon={<Iconify icon="solar:alt-arrow-right-linear" width={14} />}
                        sx={HUB_LINK_SX}
                      >
                        {brandLead} 브랜드 허브
                      </Button>
                    )}
                  </Stack>
                )}

                {(adRun || adTwin) && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {adRun && (
                      <Box sx={{ ...RUN_CHIP_SX, bgcolor: adRun.kind === 'boost' ? 'warning.lighter' : 'grey.200', color: adRun.kind === 'boost' ? 'warning.darker' : 'text.primary' }}>
                        {adRun.label}
                      </Box>
                    )}
                    {adTwin && (
                      <Box
                        component={adTwin.url ? 'a' : 'span'}
                        href={adTwin.url || undefined}
                        target={adTwin.url ? '_blank' : undefined}
                        rel={adTwin.url ? 'noopener' : undefined}
                        sx={{ ...RUN_CHIP_SX, bgcolor: 'primary.lighter', color: 'primary.dark', textDecoration: 'none', ...(adTwin.url && { cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'common.white' } }) }}
                      >
                        {adTwin.active ? '같은 소재가 Meta 광고로 집행 중' : '같은 소재가 Meta 광고에 있었음'}
                        {adTwin.pageName ? ` · ${adTwin.pageName}` : ''}
                      </Box>
                    )}
                  </Stack>
                )}
                {/* 글의 갈래(댓글 · 인용 · 리포스트 · 스토리) — 광고 표는 위 줄(`readAdRun`)이 이미 적어 여기서는 뺀다 */}
                {card.kind === 'organic' && card.post && (
                  <PostKindChips post={card.post} adTags={[]} variant="detail" stackSx={{ mb: 1 }} />
                )}
                {isStory && (
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>인스타그램 스토리에서 수집된 포스트입니다.</Typography>
                )}
                {heading && (
                  <Typography variant="subtitle2" sx={{ mb: 1, whiteSpace: paint ? 'pre-line' : undefined }}>
                    {paint ? <PaintedText text={heading} paint={paint} /> : heading}
                  </Typography>
                )}
                {body && (
                  <>
                    <Typography
                      ref={setBodyEl}
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                        ...(expanded ? {} : { display: '-webkit-box', WebkitLineClamp: CLAMP_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                      }}
                    >
                      {paint ? <PaintedText text={body} paint={paint} /> : body}
                    </Typography>
                    {(overflowing || expanded) && (
                      <Link component="button" type="button" variant="body2" onClick={() => setExpanded((v) => !v)} sx={{ mt: 0.5 }}>
                        {expanded ? '접기' : '더 보기'}
                      </Link>
                    )}
                    <PostTranslateBlock title={heading} body={body} resetKey={card.key} dense translate={translate} sx={{ mt: 1.5 }} />
                  </>
                )}
                {card.kind === 'organic' && <NegativeMentionList post={card.post} sx={{ mt: 1.5 }} />}

                {card.kind === 'ad' && (card.cta || card.landing) && (
                  <Stack
                    component="a"
                    href={card.landingUrl || card.href || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 2, px: 1.5, py: 1, borderRadius: RADIUS.sm, bgcolor: 'text.primary', color: 'common.white', textDecoration: 'none' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{card.cta || '더 알아보기'}</Typography>
                    <Typography variant="body2" noWrap sx={{ opacity: 0.7, flex: 1 }}>{card.landing}</Typography>
                    <Iconify icon="solar:alt-arrow-right-linear" width={16} />
                  </Stack>
                )}


                {card.kind === 'product' && productDetail?.product && (
                  <Box sx={{ mt: 2 }}>
                    <ProductDetailBody
                      product={productDetail.product}
                      history={productDetail.history}
                      cross={productDetail.cross}
                      detail={productDetail.detail}
                      dense
                      onCollected={productDetail.reloadDetail}
                    />
                  </Box>
                )}

                {card.kind === 'organic' && origin && (
                  <Box component={origin.link ? 'a' : 'div'} href={origin.link || undefined} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', gap: 1.5, mt: 2, p: 1.5, borderRadius: RADIUS.sm, border: 1, borderColor: 'divider', color: 'inherit', textDecoration: 'none' }}>
                    {origin.image && <Box component="img" src={origin.image} alt="" sx={{ width: 56, height: 56, borderRadius: RADIUS.sm, objectFit: 'cover', flexShrink: 0 }} />}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" component="div">원글{origin.who ? ` · ${origin.who}` : ''}</Typography>
                      <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                        {paint ? <PaintedText text={origin.text} paint={paint} /> : origin.text}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {card.kind !== 'product' && panel.metrics.length > 0 && (
                  <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: `repeat(${panel.metrics.length === 4 ? 4 : Math.min(3, panel.metrics.length)}, minmax(0, 1fr))`, gap: 1 }}>
                    {panel.metrics.map((m) => (
                      <Box key={m.label} sx={{ px: 1.5, py: 1, borderRadius: RADIUS.sm, bgcolor: 'background.neutral' }}>
                        <Typography sx={{ typography: 'uiTitle', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>{m.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/*
                  ⚠️ 상품은 **상세 본문이 없을 때만** 기본 사실을 적는다 — 본문(`ProductDetailBody`)이
                    오면 같은 값을 두 번 적게 된다. 본문은 관문 뒤 API 라 **공유 페이지에는 안 온다**.
                */}
                {(card.kind !== 'product' || !productDetail?.product) && panel.facts.length > 0 && (
                  <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 1.5 }}>
                    {panel.facts.map((f) => (
                      <Box key={f.label} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" component="div">{f.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{f.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {card.kind === 'organic' && extraSection?.(card)}

                {card.kind === 'organic' && card.post && (
                  <PostMetricTrend post={card.post} title="누적 통계 추이" useMetricDetailSort={options?.metricBasis === 'sort'} sx={{ mt: 2 }} />
                )}

                {card.kind !== 'product' && panel.tags.length > 0 && (
                  <Stack spacing={2} sx={{ mt: 2.5, pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
                    {panel.tags.map((g) => (
                      <Box key={g.title}>
                        {/* ⚠️ 누를 수 있는 묶음은 **그렇다고 적는다** — 색만 다르면 그냥 강조로 읽힌다 */}
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1, fontWeight: 600 }}>
                          {g.title}
                          {BRAND_GROUPS.has(g.title) && !publicView && (
                            <Box component="span" sx={{ ml: 0.5, fontWeight: 400, opacity: 0.8 }}>&nbsp;· 눌러서 더보기</Box>
                          )}
                        </Typography>
                        {/*
                          🔑 **브랜드 태그는 누를 수 있다** — 글을 보다가 그 브랜드의 허브로 건너뛴다.
                          ⚠️ 브랜드 묶음만 그렇다(성분·고민·해시태그는 갈 곳이 없다) — 눌리는 것처럼
                            보이면 안 되므로 손짓·밑줄도 그 묶음에만 붙인다.
                        */}
                        <Stack direction="row" flexWrap="wrap" gap={0.625}>
                          {g.tags.map((t) => (
                            <Box
                              key={t}
                              component="span"
                              role={BRAND_GROUPS.has(g.title) && !publicView ? 'button' : undefined}
                              tabIndex={BRAND_GROUPS.has(g.title) && !publicView ? 0 : undefined}
                              onClick={BRAND_GROUPS.has(g.title) && !publicView ? () => openBrandHub(t, MEDIA_DETAIL_CTA.brandTag) : undefined}
                              onKeyDown={BRAND_GROUPS.has(g.title) && !publicView ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openBrandHub(t, MEDIA_DETAIL_CTA.brandTag);
                                }
                              } : undefined}
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: RADIUS.sm,
                                bgcolor: 'background.neutral',
                                typography: 'caption',
                                fontWeight: 500,
                                color: 'text.primary',
                                ...(BRAND_GROUPS.has(g.title) && !publicView && {
                                  cursor: 'pointer',
                                  bgcolor: 'primary.lighter',
                                  color: 'primary.dark',
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: 'primary.light', color: 'common.white' },
                                }),
                              }}
                            >
                              {t}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', minHeight: 52 }}>
                {card.href && (
                  <Button size="small" variant="contained" color="inherit" component="a" href={card.href} target="_blank" rel="noopener noreferrer" startIcon={<Iconify icon="solar:square-top-down-linear" width={16} />} sx={{ flexShrink: 0 }}>
                    {card.kind === 'ad' ? '광고 라이브러리에서 보기' : card.kind === 'product' ? '상품 페이지 보기' : '원문 보기'}
                  </Button>
                )}

                <Box flexGrow={1}/>
                {/*
                  🔑 **공유는 여기 한 자리다** — 카드에는 두지 않는다. 공유하는 것이 결국 이 상세라
                    카드에도 두면 같은 일이 두 군데가 되고, 훑는 격자에 아이콘만 늘어난다.
                  🚨 갈래를 세지 않는다 — 주소를 만들 수 있나(`canShareMedia`)만 본다.
                    여기서 갈래를 또 세면 주소 쪽과 어긋난다(상품이 그렇게 빠져 있었다).
                */}
                {canShareMedia(card) && <ShareLinkButton onClick={() => onShare(card)} />}
                {/* 「캠페인 추가」 — 켠 화면(브랜드 멘션)에서 글일 때만. 권한·데모 막기는 그 부품이 쥔다 */}
                {options?.campaignAdd && !publicView && card.kind === 'organic' && card.post && (
                  <PostCardCampaignOverlay post={card.post} placement="postTable" />
                )}
                {toBookmarkTarget(card) && !publicView && !options?.hideBookmark && <PostCardBookmarkOverlay post={toBookmarkTarget(card)!} placement="postTable" disabled={Boolean(bookmarkLock)} disabledTooltip={bookmarkLock} />}
              </Stack>
            </Stack>
          </Stack>
          <SimilarStrip similar={similar} onOpenAd={onOpenSimilarAd} onOpenCard={onOpenSimilarCard} />
          </Stack>
        </>
      )}
    </Dialog>
  );
}
